<template>
  <article class="invoice-create-card invoice-create-card--accent-billing payment-receipt-document-card">
    <header class="invoice-create-card__head invoice-create-card__head--space">
      <h3>
        <i class="fa fa-user-o invoice-create-section-icon invoice-create-section-icon--billing" aria-hidden="true"></i>
        {{ text?.create?.ui?.documentSupplierCard?.title || 'Documento / Proveedor' }}
      </h3>

      <div class="payment-receipt-document-card__currency-wrap">
        <AppCurrencyControl
          v-model="currencyModel"
          :exchange-rate="exchangeRate"
          @update:exchange-rate="$emit('update:exchange-rate', $event)"
        />
      </div>
    </header>

    <div class="invoice-create-info-grid payment-receipt-document-card__grid payment-receipt-document-card__grid--toggles">
      <div class="invoice-create-field app-modal-field payment-receipt-document-card__voucher-field">
        <span>{{ text?.create?.ui?.documentSupplierCard?.fields?.voucherType || 'Tipo de comprobante' }}</span>
        <div class="payment-receipt-document-card__voucher-toggle">
          <label class="app-modal-toggle payment-receipt-document-card__voucher-item">
            <input
              type="checkbox"
              :checked="isCfdiMode"
              @change="setVoucherType('cfdi')"
            />
            <span>CFDI</span>
          </label>

          <label class="app-modal-toggle payment-receipt-document-card__voucher-item">
            <input
              type="checkbox"
              :checked="isSimpleNoteVoucher"
              @change="setVoucherType('simple_note')"
            />
            <span>Nota Simple</span>
          </label>
        </div>
      </div>

      <div class="invoice-create-field app-modal-field payment-receipt-document-card__voucher-field">
        <span>{{ text?.create?.ui?.documentSupplierCard?.fields?.payTo || 'Pago a' }}</span>
        <div class="payment-receipt-document-card__voucher-toggle">
          <label class="app-modal-toggle payment-receipt-document-card__voucher-item">
            <input
              type="checkbox"
              :checked="payToSupplier"
              @change="setPayTo('supplier')"
            />
            <span>Proveedor</span>
          </label>

          <label class="app-modal-toggle payment-receipt-document-card__voucher-item">
            <input
              type="checkbox"
              :checked="!payToSupplier"
              @change="setPayTo('collaborator')"
            />
            <span>Colaborador</span>
          </label>
        </div>
      </div>
    </div>

    <div class="invoice-create-info-grid payment-receipt-document-card__grid payment-receipt-document-card__grid--selectors">
      <div class="invoice-create-field app-modal-field">
        <span>{{ counterpartLabel }}</span>
        <multiselect
          v-model="selectedCounterpartModel"
          :options="counterpartOptions"
          track-by="value"
          label="label"
          :custom-label="counterpartOptionLabel"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          class="app-modal-select"
          :placeholder="counterpartPlaceholder"
        >
          <template #option="{ option }">
            <div class="payment-receipt-document-card__supplier-option">
              <strong>{{ option?.label || '-' }}</strong>
              <small>{{ text?.create?.ui?.documentSupplierCard?.supplierPendingPrefix || 'Pendiente' }} {{ currency }}: {{ formatMoney(resolveSupplierPending(option)) }}</small>
            </div>
          </template>
          <template #singleLabel="{ option }">
            <div class="payment-receipt-document-card__supplier-option payment-receipt-document-card__supplier-option--single">
              <strong>{{ option?.label || '-' }}</strong>
            </div>
          </template>
          <template #noResult>
            <span>{{ text?.create?.ui?.documentSupplierCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
          <template #noOptions>
            <span>{{ text?.create?.ui?.documentSupplierCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
        </multiselect>
      </div>

      <div class="invoice-create-field app-modal-field payment-receipt-document-card__pending-wrap">
        <span>{{ text?.create?.ui?.documentSupplierCard?.fields?.pendingAmount || 'Monto Pendiente' }}</span>
        <strong class="payment-receipt-document-card__pending-amount">
          {{ formatMoney(supplierPendingAmount) }}
        </strong>
      </div>
    </div>
  </article>
</template>

<script>
import AppCurrencyControl from '@/components/common/AppCurrencyControl.vue';
import { formatCurrency } from '@/utils/numberFormat';

export default {
  name: 'ExpenseReceiptCreateDocumentSupplierCard',
  components: {
    AppCurrencyControl,
  },
  props: {
    text: {
      type: Object,
      default: () => ({}),
    },
    documentTypeOptions: {
      type: Array,
      default: () => [],
    },
    supplierOptions: {
      type: Array,
      default: () => [],
    },
    selectedSupplier: {
      type: Object,
      default: null,
    },
    collaboratorOptions: {
      type: Array,
      default: () => [],
    },
    selectedCollaborator: {
      type: Object,
      default: null,
    },
    info: {
      type: Object,
      required: true,
    },
    currency: {
      type: String,
      default: 'MXN',
    },
    exchangeRate: {
      type: Number,
      default: 1,
    },
    supplierPendingAmount: {
      type: Number,
      default: 0,
    },
    payTo: {
      type: String,
      default: 'supplier',
    },
  },
  emits: ['select-supplier', 'select-collaborator', 'info-change', 'update:currency', 'update:exchange-rate'],
  computed: {
    isCfdiMode() {
      return String(this.info?.voucherType || 'cfdi').trim().toLowerCase() === 'cfdi';
    },
    isSimpleNoteVoucher() {
      return String(this.info?.voucherType || '').trim().toLowerCase() === 'simple_note';
    },
    payToSupplier() {
      return String(this.payTo || 'supplier').trim().toLowerCase() !== 'collaborator';
    },
    counterpartOptions() {
      return this.payToSupplier ? this.supplierOptions : this.collaboratorOptions;
    },
    counterpartLabel() {
      return this.payToSupplier
        ? (this.text?.create?.ui?.documentSupplierCard?.fields?.supplier || 'Proveedor')
        : (this.text?.create?.ui?.documentSupplierCard?.fields?.collaborator || 'Colaborador');
    },
    counterpartPlaceholder() {
      return this.payToSupplier
        ? (this.text?.create?.ui?.documentSupplierCard?.placeholders?.supplier || 'Selecciona proveedor')
        : (this.text?.create?.ui?.documentSupplierCard?.placeholders?.collaborator || 'Selecciona colaborador');
    },
    selectedCounterpartModel: {
      get() {
        return this.payToSupplier ? this.selectedSupplier : this.selectedCollaborator;
      },
      set(value) {
        if (this.payToSupplier) {
          this.$emit('select-supplier', value || null);
          return;
        }

        this.$emit('select-collaborator', value || null);
      },
    },
    currencyModel: {
      get() {
        return this.currency;
      },
      set(value) {
        this.$emit('update:currency', String(value || '').trim().toUpperCase() || 'MXN');
      },
    },
  },
  methods: {
    setVoucherType(type) {
      this.$emit('info-change', { key: 'voucherType', value: String(type || 'cfdi').trim().toLowerCase() });
    },
    setPayTo(value) {
      this.$emit('info-change', { key: 'payTo', value: String(value || 'supplier').trim().toLowerCase() });
    },
    resolveSupplierPending(option) {
      const pendingByCurrency = option?.meta?.pendingByCurrency && typeof option.meta.pendingByCurrency === 'object'
        ? option.meta.pendingByCurrency
        : {};
      return Number(pendingByCurrency?.[this.currency] || 0) || 0;
    },
    counterpartOptionLabel(option) {
      const amount = this.resolveSupplierPending(option);
      const name = String(option?.label || '').trim();
      return `${name} - ${this.formatMoney(amount)}`;
    },
    formatMoney(amount) {
      return formatCurrency(amount, { currency: this.currency });
    },
  },
};
</script>

<style scoped>
.payment-receipt-document-card {
  display: grid;
  gap: 8px;
  align-content: start;
  padding-top: 10px;
}

.payment-receipt-document-card .invoice-create-card__head {
  align-items: flex-start;
  margin-bottom: 6px;
}

.payment-receipt-document-card__currency-wrap {
  width: 230px;
  max-width: 100%;
  display: flex;
  justify-content: flex-end;
}

.payment-receipt-document-card__grid {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.payment-receipt-document-card__grid--toggles {
  align-items: start;
}

.payment-receipt-document-card__grid--selectors {
  grid-template-columns: minmax(0, 1.25fr) minmax(190px, 0.75fr);
}

.payment-receipt-document-card__voucher-toggle {
  display: flex;
  gap: 10px;
  min-height: 40px;
  align-items: center;
}

.payment-receipt-document-card__voucher-item {
  margin: 0;
}

.payment-receipt-document-card__voucher-item span {
  white-space: nowrap;
}

.payment-receipt-document-card__supplier-option {
  display: grid;
  gap: 1px;
}

.payment-receipt-document-card__supplier-option strong {
  font-size: 12px;
  color: #0f172a;
}

.payment-receipt-document-card__supplier-option small {
  font-size: 11px;
  color: #64748b;
}

.payment-receipt-document-card__supplier-option--single small {
  display: none;
}

.payment-receipt-document-card__pending-wrap {
  border: 0;
  border-radius: 0;
  padding: 0;
  background: transparent;
  width: 100%;
  max-width: 230px;
  justify-self: end;
}

.payment-receipt-document-card__pending-amount {
  display: block;
  margin-top: 4px;
  font-size: 18px;
  color: #0f172a;
  text-align: right;
}

@media (max-width: 1200px) {
  .payment-receipt-document-card__grid--toggles,
  .payment-receipt-document-card__grid--selectors {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 767px) {
  .payment-receipt-document-card__grid--toggles,
  .payment-receipt-document-card__grid--selectors {
    grid-template-columns: 1fr;
  }

  .payment-receipt-document-card__currency-wrap {
    width: 100%;
    max-width: 100%;
  }
}
</style>
